import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Pill } from '../components/Pill';
import { Flag } from '../components/Flag';
import { Ic } from '../components/Icon';
import { matchesApi } from '../api/matches';
import { betsApi } from '../api/bets';
import { teamNamePl } from '../lib/flags';
import { phaseLabel } from '../lib/phase';
import { timeUntil, formatKickoff, formatDateOnly, formatTime } from '../lib/date';
import { pickDisplayScore } from '../lib/score';
import type { HttpError } from '../api/client';
import type { PredictionTally, TeamHistory, Match } from '../api/types';

export function BetPage() {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const fromUrl = (location.state as { from?: string } | null)?.from ?? '/calendar';

  const match = useQuery({
    queryKey: ['match', matchId],
    queryFn: () => matchesApi.detail(matchId!),
    enabled: !!matchId,
  });

  const myBet = useQuery({
    queryKey: ['my-bet', matchId],
    queryFn: () => betsApi.forMatch(matchId!),
    enabled: !!matchId,
  });

  // "How others predicted" — only meaningful once betting is closed (LIVE/FINISHED).
  // The backend returns an empty list for any other status.
  const summary = useQuery({
    queryKey: ['bet-summary', matchId],
    queryFn: () => betsApi.summary(matchId!),
    enabled: !!matchId && (match.data?.status === 'LIVE' || match.data?.status === 'FINISHED'),
  });

  // Previous tournament matches of both teams — shown in the same slot for UPCOMING matches.
  const history = useQuery({
    queryKey: ['team-history', matchId],
    queryFn: () => matchesApi.teamHistory(matchId!),
    enabled: !!matchId && match.data?.status === 'UPCOMING',
  });

  const [home, setHome] = useState(0);
  const [away, setAway] = useState(0);
  // Penalty-shootout prediction — only used for a knockout match predicted as a draw.
  // Starts at 0–0 (like the result steppers); the user must pick a winner to save.
  const [penHome, setPenHome] = useState(0);
  const [penAway, setPenAway] = useState(0);

  useEffect(() => {
    if (myBet.data) {
      setHome(myBet.data.homeScore);
      setAway(myBet.data.awayScore);
      if (myBet.data.penaltyHomeScore != null && myBet.data.penaltyAwayScore != null) {
        setPenHome(myBet.data.penaltyHomeScore);
        setPenAway(myBet.data.penaltyAwayScore);
      }
    }
  }, [myBet.data]);

  const place = useMutation({
    mutationFn: () => {
      const mm = match.data!;
      const sendPens = mm.phase !== 'GROUP' && home === away;
      return betsApi.placeOrUpdate(
        matchId!,
        home,
        away,
        sendPens ? penHome : null,
        sendPens ? penAway : null,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-bet', matchId] });
      queryClient.invalidateQueries({ queryKey: ['my-bets'] });
      const sep = fromUrl.includes('?') ? '&' : '?';
      navigate(`${fromUrl}${sep}match=${matchId}`);
    },
  });

  if (!match.data) return <DesktopShell><TopBar title="Ładowanie meczu…" /></DesktopShell>;
  const m = match.data;
  const pending = m.status === 'PENDING_DRAW';
  const closed = m.status !== 'UPCOMING' || new Date(m.kickoff) <= new Date();
  const homeLabel = m.homePlaceholder ?? teamNamePl(m.homeTeam);
  const awayLabel = m.awayPlaceholder ?? teamNamePl(m.awayTeam);
  const homeFlag = m.homePlaceholder ?? m.homeTeam;
  const awayFlag = m.awayPlaceholder ?? m.awayTeam;
  const winnerLabel =
    home > away ? `${homeLabel.toUpperCase()} WYGRA` : home < away ? `${awayLabel.toUpperCase()} WYGRA` : 'REMIS';
  const matchScore = pickDisplayScore(m);
  // Banner shows match score for closed matches, user's bet selection while form is open.
  const showMatchScore = closed && !pending && matchScore != null;
  // Knockout draws go to penalties: show the shootout picker and require a winner.
  const isKnockout = m.phase !== 'GROUP';
  const isDraw = home === away;
  const showPenaltyPicker = isKnockout && isDraw;
  const penaltyNeedsWinner = showPenaltyPicker && penHome === penAway;

  return (
    <DesktopShell>
      <TopBar
        title="Obstaw mecz"
        subtitle={`${phaseLabel(m.phase, m.group)} · ${formatKickoff(m.kickoff)} · ${m.venue ?? ''}`}
      />

      <div
        className="stack-on-mobile"
        style={{
          flex: 1,
          padding: '28px 36px',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: 24,
        }}
      >
        <Card padding={0} style={{ overflow: 'hidden' }}>
          <div
            style={{
              background: 'linear-gradient(135deg,#0f1e3d 0%,#1a2744 100%)',
              color: '#fff',
              padding: '32px 28px 28px',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                alignItems: 'center',
                gap: 12,
                marginBottom: 18,
              }}
            >
              <Pill
                color="accent"
                style={{ background: 'var(--accent)', color: '#3a2a00' }}
              >
                {phaseLabel(m.phase, m.group).toUpperCase()}
              </Pill>
              {m.venue && (
                <div
                  title={m.venue}
                  style={{
                    fontSize: 12,
                    color: 'rgba(255,255,255,.7)',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {m.venue}
                </div>
              )}
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,.6)', whiteSpace: 'nowrap' }}>
                {formatDateOnly(m.kickoff)}, {formatTime(m.kickoff)}
              </div>
            </div>
            {!closed && (
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <Pill
                  color="accent"
                  style={{
                    background: 'var(--accent)',
                    color: '#0f1e3d',
                    fontSize: 12,
                    padding: '5px 12px',
                    boxShadow: '0 2px 8px rgba(244,185,58,.35)',
                  }}
                >
                  <Ic.clock size={11} />
                  Do startu: {timeUntil(m.kickoff)}
                </Pill>
              </div>
            )}
            <div
              className="bet-banner-row"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                gridTemplateRows: 'auto auto',
                columnGap: 20,
                rowGap: 12,
                alignItems: 'center',
              }}
            >
              <div style={{ justifySelf: 'start' }}>
                <Flag code={homeFlag} size={72} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div
                  className="serif num bet-banner-score"
                  style={{
                    fontSize: 72,
                    fontWeight: 700,
                    letterSpacing: '-0.03em',
                    color: 'var(--accent)',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {showMatchScore ? matchScore!.mainHome : home}
                  <span style={{ color: 'rgba(255,255,255,.3)', margin: '0 14px' }}>
                    {showMatchScore ? '–' : ':'}
                  </span>
                  {showMatchScore ? matchScore!.mainAway : away}
                </div>
                {showMatchScore && matchScore!.hadExtraTime && (
                  <>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,.75)', fontStyle: 'italic', marginTop: 10 }}>
                      po dogrywce
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>
                      {[
                        `${m.homeScore}-${m.awayScore} w 90'`,
                        matchScore!.hadPenalties ? `${m.penaltyHomeScore}-${m.penaltyAwayScore} karne` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  </>
                )}
              </div>
              <div style={{ justifySelf: 'end' }}>
                <Flag code={awayFlag} size={72} />
              </div>
              <div className="serif" style={{ fontSize: 22, fontWeight: 700, textAlign: 'left' }}>
                {homeLabel}
              </div>
              <div />
              <div className="serif" style={{ fontSize: 22, fontWeight: 700, textAlign: 'right' }}>
                {awayLabel}
              </div>
            </div>
          </div>

          {pending ? (
            <div style={{ padding: 28, textAlign: 'center', color: '#5b6578' }}>
              Drużyny dla tego meczu nie są jeszcze znane. Typowanie otworzy się, gdy zakończą się wcześniejsze rundy.{' '}
              <Link to="/calendar" style={{ color: '#0f1e3d', fontWeight: 600 }}>
                Wróć do kalendarza
              </Link>
              .
            </div>
          ) : closed ? (
            <div style={{ padding: '24px 24px 22px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <BetOutcomeBox label="TWÓJ TYP">
                  {myBet.data ? (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span className="serif num" style={{ fontSize: 28, fontWeight: 700, color: '#0f1e3d', lineHeight: 1 }}>
                        {myBet.data.homeScore}–{myBet.data.awayScore}
                      </span>
                      {myBet.data.penaltyHomeScore != null && myBet.data.penaltyAwayScore != null && (
                        <span className="num" style={{ fontSize: 13, fontWeight: 600, color: '#6b4a00' }}>
                          k. {myBet.data.penaltyHomeScore}–{myBet.data.penaltyAwayScore}
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="serif num" style={{ fontSize: 28, fontWeight: 700, color: 'rgba(15,30,61,.35)', lineHeight: 1 }}>
                      —
                    </span>
                  )}
                </BetOutcomeBox>
                <PointsBox bet={myBet.data ?? undefined} status={m.status} />
              </div>
              <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: '#5b6578' }}>
                {m.status === 'FINISHED' ? 'Mecz zakończony.' : 'Typowanie zostało zamknięte.'}{' '}
                <Link to="/calendar" style={{ color: '#0f1e3d', fontWeight: 600 }}>
                  Wróć do kalendarza
                </Link>
                .
              </div>
            </div>
          ) : (
            <div style={{ padding: '28px 28px 24px' }}>
              <div
                style={{
                  fontSize: 11,
                  color: '#5b6578',
                  letterSpacing: '.06em',
                  fontWeight: 600,
                  marginBottom: 14,
                }}
              >
                TWÓJ TYP
              </div>
              <div className="bet-steppers" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <Stepper code={homeFlag} label={homeLabel} value={home} onChange={setHome} />
                <Stepper code={awayFlag} label={awayLabel} value={away} onChange={setAway} />
              </div>

              {showPenaltyPicker && (
                <div style={{ marginTop: 20 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6b4a00',
                      letterSpacing: '.06em',
                      fontWeight: 600,
                      marginBottom: 4,
                    }}
                  >
                    RZUTY KARNE
                  </div>
                  <div style={{ fontSize: 11.5, color: '#5b6578', marginBottom: 12, lineHeight: 1.5 }}>
                    Typujesz remis — w pucharze o awansie zdecydują karne. Wskaż wynik serii.
                  </div>
                  <div className="bet-steppers" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    <Stepper code={homeFlag} label={homeLabel} value={penHome} onChange={setPenHome} />
                    <Stepper code={awayFlag} label={awayLabel} value={penAway} onChange={setPenAway} />
                  </div>
                  {penaltyNeedsWinner && (
                    <div style={{ marginTop: 10, fontSize: 12, color: '#8a2a22', textAlign: 'center' }}>
                      Karne muszą mieć zwycięzcę — wynik nie może być remisowy.
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(15,30,61,.06)' }}>
                <div
                  style={{
                    fontSize: 11,
                    color: '#5b6578',
                    letterSpacing: '.06em',
                    fontWeight: 600,
                    marginBottom: 10,
                    textAlign: 'center',
                  }}
                >
                  SZYBKIE TYPY
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginBottom: 16,
                  }}
                >
                  {[[1, 0], [2, 0], [2, 1], [1, 1], [0, 0], [1, 2], [0, 1]].map(([x, y]) => {
                    const active = x === home && y === away;
                    return (
                      <button
                        key={`${x}-${y}`}
                        onClick={() => {
                          setHome(x);
                          setAway(y);
                        }}
                        className="mono"
                        style={{
                          height: 36,
                          padding: '0 14px',
                          borderRadius: 10,
                          border: '1px solid',
                          borderColor: active ? '#0f1e3d' : 'rgba(15,30,61,.12)',
                          background: active ? '#0f1e3d' : '#fff',
                          color: active ? '#fff' : '#0f1e3d',
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: 'pointer',
                          fontFamily: 'Geist Mono, monospace',
                        }}
                      >
                        {x}–{y}
                      </button>
                    );
                  })}
                </div>
                <div className="bet-actions-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                  <Btn
                    variant="danger"
                    size="lg"
                    onClick={() => navigate(-1)}
                    className="bet-cancel-btn"
                    style={{ minWidth: 160 }}
                  >
                    Anuluj
                  </Btn>
                  <Btn
                    variant="accent"
                    size="lg"
                    icon={<Ic.check size={14} />}
                    onClick={() => place.mutate()}
                    disabled={place.isPending || penaltyNeedsWinner}
                    className="bet-save-btn"
                    style={{ minWidth: 160 }}
                  >
                    {place.isPending ? 'Zapisywanie…' : 'Zapisz typ'}
                  </Btn>
                </div>
              </div>

              {place.isError && (
                <div
                  style={{
                    marginTop: 14,
                    padding: 12,
                    background: 'rgba(208,70,60,.08)',
                    color: '#8a2a22',
                    borderRadius: 10,
                    fontSize: 13,
                  }}
                >
                  {(place.error as HttpError).body?.message ?? 'Nie udało się zapisać typu.'}
                </div>
              )}
            </div>
          )}
        </Card>

        {!pending && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <FifaRankCard
            homeLabel={homeLabel}
            awayLabel={awayLabel}
            homeFlag={homeFlag}
            awayFlag={awayFlag}
            homeRank={m.homeTeamRank}
            awayRank={m.awayTeamRank}
          />
          {m.status === 'UPCOMING' && history.data && (
            <PrevMatchesCard history={history.data} homeCode={homeFlag} awayCode={awayFlag} />
          )}
          {!closed && (
          <Card padding={18}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                Twoje potencjalne punkty
              </h4>
              <Ic.bolt size={14} style={{ color: 'var(--accent)' }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
              <div style={{ padding: '10px 12px', background: 'rgba(31,157,85,.08)', borderRadius: 10 }}>
                <div style={{ fontSize: 10, color: '#0f6b34', fontWeight: 600, letterSpacing: '.06em' }}>
                  JEŚLI {home}–{away}
                </div>
                <div className="serif num" style={{ fontSize: 24, fontWeight: 700, color: '#0f6b34' }}>
                  +3
                </div>
                <div style={{ color: '#0f6b34', fontSize: 11 }}>dokładny wynik</div>
              </div>
              <div style={{ padding: '10px 12px', background: 'rgba(244,185,58,.12)', borderRadius: 10 }}>
                <div style={{ fontSize: 10, color: '#6b4a00', fontWeight: 600, letterSpacing: '.06em' }}>
                  JEŚLI {winnerLabel}
                </div>
                <div className="serif num" style={{ fontSize: 24, fontWeight: 700, color: '#6b4a00' }}>
                  +1
                </div>
                <div style={{ color: '#6b4a00', fontSize: 11 }}>tylko rezultat</div>
              </div>
            </div>
            {isKnockout && (
              <div
                style={{
                  marginTop: 10,
                  padding: 12,
                  background: 'rgba(244,185,58,.12)',
                  border: '1px solid rgba(244,185,58,.35)',
                  borderRadius: 10,
                  fontSize: 12,
                  opacity: showPenaltyPicker ? 1 : 0.85,
                }}
              >
                <div style={{ fontSize: 10, color: '#6b4a00', fontWeight: 600, letterSpacing: '.06em', marginBottom: 8 }}>
                  {showPenaltyPicker ? `KARNE — TWÓJ TYP ${penHome}–${penAway}` : 'KARNE'}
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                  <span className="serif num" style={{ fontSize: 18, fontWeight: 700, color: '#0f6b34', minWidth: 26 }}>+2</span>
                  <span style={{ color: '#6b4a00' }}>za dokładny wynik serii</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span className="serif num" style={{ fontSize: 18, fontWeight: 700, color: '#6b4a00', minWidth: 26 }}>+1</span>
                  <span style={{ color: '#6b4a00' }}>za samego zwycięzcę serii</span>
                </div>
                <div style={{ color: '#8a7330', fontSize: 11, marginTop: 8, lineHeight: 1.45 }}>
                  {showPenaltyPicker
                    ? 'Sekcja aktywna, bo typujesz remis.'
                    : 'Pojawią się do wytypowania, gdy ustawisz remis.'}
                </div>
              </div>
            )}
            <div style={{ fontSize: 11.5, color: '#5b6578', marginTop: 12, lineHeight: 1.5 }}>
              <ScoringRuleNote isKnockout={isKnockout} />
            </div>
          </Card>
          )}
          {(m.status === 'LIVE' || m.status === 'FINISHED') && (
            <OthersBetsCard
              tallies={summary.data ?? []}
              loading={summary.isLoading}
              resultHome={m.homeScore}
              resultAway={m.awayScore}
              myHome={myBet.data?.homeScore ?? null}
              myAway={myBet.data?.awayScore ?? null}
            />
          )}
          {closed && (
            <ScoringRulesCard isKnockout={isKnockout} />
          )}
        </div>
        )}
      </div>
    </DesktopShell>
  );
}

// One scoring rule line: the points badge plus its description.
function RuleRow({ pts, label, accent }: { pts: string; label: string; accent: 'green' | 'gold' | 'gray' }) {
  const color = accent === 'green' ? '#0f6b34' : accent === 'gold' ? '#6b4a00' : '#5b6578';
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
      <span className="serif num" style={{ fontSize: 18, fontWeight: 700, color, minWidth: 30 }}>{pts}</span>
      <span style={{ fontSize: 12.5, color: '#3a4252' }}>{label}</span>
    </div>
  );
}

// Static scoring-rules explainer shown for live/finished matches (no points are
// computed here). The rules differ for group vs knockout phase.
function ScoringRulesCard({ isKnockout }: { isKnockout: boolean }) {
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Zasady punktacji
        </h4>
        <Ic.bolt size={14} style={{ color: 'var(--accent)' }} />
      </div>

      <div style={{ fontSize: 10, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em', marginBottom: 8 }}>
        WYNIK {isKnockout ? "(PO DOGRYWCE, 120')" : "(90')"}
      </div>
      <RuleRow pts="3" label="dokładny wynik" accent="green" />
      <RuleRow pts="1" label="trafiony rezultat (zwycięzca lub remis)" accent="gold" />
      <RuleRow pts="0" label="nietrafiony rezultat" accent="gray" />

      {isKnockout && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid rgba(15,30,61,.06)' }}>
          <div style={{ fontSize: 10, color: '#6b4a00', fontWeight: 600, letterSpacing: '.06em', marginBottom: 8 }}>
            KARNE (GDY MECZ KOŃCZY SIĘ REMISEM)
          </div>
          <RuleRow pts="+2" label="dokładny wynik serii karnych" accent="green" />
          <RuleRow pts="+1" label="trafiony zwycięzca serii karnych" accent="gold" />
        </div>
      )}

      <div style={{ fontSize: 11.5, color: '#5b6578', marginTop: 14, lineHeight: 1.5 }}>
        <ScoringRuleNote isKnockout={isKnockout} />
      </div>
    </Card>
  );
}

// Single source of truth for the scoring-rule footnote, shared by the open-bet
// and closed-match panels so both read identically.
function ScoringRuleNote({ isKnockout }: { isKnockout: boolean }) {
  return isKnockout ? (
    <>
      Liczymy <b style={{ color: '#0f1e3d' }}>wynik ostateczny — łącznie z dogrywką (120')</b>. Remis rozstrzygają karne.
    </>
  ) : (
    <>
      Liczymy wynik w <b style={{ color: '#0f1e3d' }}>podstawowym czasie gry (90')</b>. W fazie grupowej nie ma dogrywki ani karnych.
    </>
  );
}

// Anonymous "how others predicted" card, shown for LIVE/FINISHED matches. Lists each
// distinct predicted score with a popularity bar (sized against the most-picked score),
// highlighting the row matching the actual match score (green) and the user's own pick (gold).
function OthersBetsCard({
  tallies,
  loading,
  resultHome,
  resultAway,
  myHome,
  myAway,
}: {
  tallies: PredictionTally[];
  loading: boolean;
  resultHome: number | null;
  resultAway: number | null;
  myHome: number | null;
  myAway: number | null;
}) {
  const total = tallies.reduce((s, t) => s + t.count, 0);
  const maxCount = tallies.reduce((m, t) => Math.max(m, t.count), 0);
  const hasResult = resultHome != null && resultAway != null;
  const hasMine = myHome != null && myAway != null;

  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Jak typowali inni
        </h4>
        <Ic.users size={14} style={{ color: 'var(--accent)' }} />
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: '#5b6578' }}>Ładowanie…</div>
      ) : tallies.length === 0 ? (
        <div style={{ fontSize: 13, color: '#5b6578' }}>Nikt jeszcze nie obstawił tego meczu.</div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {tallies.map((t) => {
              const isResult = hasResult && t.homeScore === resultHome && t.awayScore === resultAway;
              const isMine = hasMine && t.homeScore === myHome && t.awayScore === myAway;
              const pct = maxCount > 0 ? Math.max(6, (t.count / maxCount) * 100) : 0;
              const color = isResult ? '#0f6b34' : isMine ? '#6b4a00' : '#3a4252';
              const barColor = isResult ? 'rgba(31,157,85,.55)' : isMine ? 'var(--accent)' : 'rgba(15,30,61,.20)';
              const bg = isResult ? 'rgba(31,157,85,.10)' : isMine ? 'rgba(244,185,58,.12)' : 'transparent';
              return (
                <div
                  key={`${t.homeScore}-${t.awayScore}`}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 8px', borderRadius: 8, background: bg }}
                >
                  <span
                    className="serif num"
                    style={{
                      display: 'inline-grid',
                      gridTemplateColumns: '1fr auto 1fr',
                      alignItems: 'baseline',
                      columnGap: 2,
                      width: 40,
                      fontSize: 15,
                      fontWeight: 700,
                      color,
                    }}
                  >
                    <span style={{ textAlign: 'right' }}>{t.homeScore}</span>
                    <span>–</span>
                    <span style={{ textAlign: 'left' }}>{t.awayScore}</span>
                  </span>
                  <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(15,30,61,.06)', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4 }} />
                  </div>
                  <span className="num" style={{ fontSize: 13, fontWeight: 700, color, minWidth: 16, textAlign: 'right' }}>
                    {t.count}
                  </span>
                </div>
              );
            })}
          </div>

          <div
            style={{
              marginTop: 12,
              paddingTop: 10,
              borderTop: '1px solid rgba(15,30,61,.06)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 8,
              fontSize: 11,
              color: '#5b6578',
            }}
          >
            <span>{total} {typyLabel(total)}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {hasResult && <LegendDot color="rgba(31,157,85,.55)" label="wynik meczu" />}
              {hasMine && <LegendDot color="var(--accent)" label="twój typ" />}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      {label}
    </span>
  );
}

// Polish plural for "typ": 1 → typ, 2-4 → typy, else → typów.
function typyLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return 'typ';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'typy';
  return 'typów';
}

// Previous tournament matches of both teams, shown before an upcoming match. Each match is a
// single line — subject team on the left, opponent on the right, score in the middle — tinted
// win / draw / loss from the subject team's perspective. Knockout games decided in a shootout
// are judged by the penalty result.
function PrevMatchesCard({
  history,
  homeCode,
  awayCode,
}: {
  history: TeamHistory;
  homeCode: string;
  awayCode: string;
}) {
  const rows = [
    ...history.home.map((match) => ({ match, teamCode: homeCode })),
    ...history.away.map((match) => ({ match, teamCode: awayCode })),
  ];
  if (rows.length === 0) return null;
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Wcześniejsze mecze
        </h4>
        <Ic.ball size={14} style={{ color: 'var(--accent)' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(({ match, teamCode }) => (
          <PrevMatchRow key={`${teamCode}-${match.id}`} match={match} teamCode={teamCode} />
        ))}
      </div>
    </Card>
  );
}

function PrevMatchRow({ match, teamCode }: { match: Match; teamCode: string }) {
  const ds = pickDisplayScore(match);
  const isHome = match.homeTeam === teamCode;
  const oppCode = isHome ? match.awayTeam : match.homeTeam;
  const teamScore = ds ? (isHome ? ds.mainHome : ds.mainAway) : null;
  const oppScore = ds ? (isHome ? ds.mainAway : ds.mainHome) : null;
  const hadPens = !!ds?.hadPenalties && match.penaltyHomeScore != null && match.penaltyAwayScore != null;

  let outcome: 'win' | 'draw' | 'loss' | 'unknown';
  if (teamScore == null || oppScore == null) {
    outcome = 'unknown';
  } else if (hadPens) {
    const teamPen = isHome ? (match.penaltyHomeScore as number) : (match.penaltyAwayScore as number);
    const oppPen = isHome ? (match.penaltyAwayScore as number) : (match.penaltyHomeScore as number);
    outcome = teamPen > oppPen ? 'win' : 'loss';
  } else {
    outcome = teamScore > oppScore ? 'win' : teamScore < oppScore ? 'loss' : 'draw';
  }

  const palette = {
    win: { bg: 'rgba(31,157,85,.10)', color: '#0f6b34' },
    draw: { bg: 'rgba(15,30,61,.04)', color: '#5b6578' },
    loss: { bg: 'rgba(208,70,60,.08)', color: '#8a2a22' },
    unknown: { bg: 'transparent', color: '#5b6578' },
  }[outcome];
  const annotation = ds?.hadPenalties ? 'k.' : ds?.hadExtraTime ? 'dogr.' : null;

  const nameStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: '#0f1e3d',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
        alignItems: 'center',
        gap: 8,
        padding: '6px 8px',
        borderRadius: 8,
        background: palette.bg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <Flag code={teamCode} size={20} />
        <span style={nameStyle}>{teamNamePl(teamCode)}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
        <span className="serif num" style={{ fontSize: 14, fontWeight: 700, color: palette.color, whiteSpace: 'nowrap' }}>
          {teamScore ?? '–'}–{oppScore ?? '–'}
        </span>
        {annotation && <span style={{ fontSize: 10, color: '#8a93a5' }}>{annotation}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, minWidth: 0 }}>
        <span style={{ ...nameStyle, textAlign: 'right' }}>{teamNamePl(oppCode)}</span>
        <Flag code={oppCode} size={20} />
      </div>
    </div>
  );
}

function Stepper({ code, label, value, onChange }: { code: string; label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div className="stepper" style={{ padding: '18px 18px 16px', background: '#f7f6f2', borderRadius: 12, border: '1px solid rgba(15,30,61,.05)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Flag code={code} size={24} />
        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
      </div>
      <div className="stepper-controls" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          type="button"
          onClick={() => onChange(Math.max(0, value - 1))}
          className="stepper-minus"
          style={btnCircle}
        >
          −
        </button>
        <div
          className="serif num stepper-value"
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: 56,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: '#0f1e3d',
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        <button
          type="button"
          onClick={() => onChange(Math.min(15, value + 1))}
          className="stepper-plus"
          style={{ ...btnCircle, background: 'var(--accent)', color: '#3a2a00' }}
        >
          +
        </button>
      </div>
    </div>
  );
}

const btnCircle: React.CSSProperties = {
  width: 48,
  height: 48,
  borderRadius: 24,
  border: 'none',
  cursor: 'pointer',
  background: '#fff',
  color: '#0f1e3d',
  fontSize: 22,
  fontWeight: 600,
  boxShadow: '0 1px 3px rgba(15,30,61,.12), inset 0 0 0 1px rgba(15,30,61,.06)',
};

function BetOutcomeBox({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'rgba(15,30,61,.04)', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em', marginBottom: 6 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function FifaRankCard({
  homeLabel,
  awayLabel,
  homeFlag,
  awayFlag,
  homeRank,
  awayRank,
}: {
  homeLabel: string;
  awayLabel: string;
  homeFlag: string;
  awayFlag: string;
  homeRank: number | null;
  awayRank: number | null;
}) {
  const diff =
    homeRank != null && awayRank != null ? Math.abs(homeRank - awayRank) : null;
  return (
    <Card padding={18}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h4 className="serif" style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Ranking FIFA
        </h4>
        <Ic.trophy size={14} style={{ color: 'var(--accent)' }} />
      </div>
      <RankRow flag={homeFlag} label={homeLabel} rank={homeRank} />
      <div style={{ height: 1, background: 'rgba(15,30,61,.06)', margin: '10px 0' }} />
      <RankRow flag={awayFlag} label={awayLabel} rank={awayRank} />
      <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(15,30,61,.06)', fontSize: 12, color: '#5b6578' }}>
        {diff != null && (
          <div>{diff === 0 ? 'Równe miejsce w rankingu' : `Różnica ${diff} ${miejscaLabel(diff)} w rankingu`}</div>
        )}
        <div style={{ fontSize: 11, color: '#8a93a5', marginTop: diff != null ? 4 : 0 }}>
          Stan na 11 czerwca 2026
        </div>
      </div>
    </Card>
  );
}

function RankRow({ flag, label, rank }: { flag: string; label: string; rank: number | null }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Flag code={flag} size={24} />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0f1e3d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </span>
      </div>
      <span className="serif num" style={{ fontSize: 18, fontWeight: 700, color: rank != null ? '#0f1e3d' : 'rgba(15,30,61,.35)', whiteSpace: 'nowrap' }}>
        {rank != null ? `#${rank}` : '—'}
      </span>
    </div>
  );
}

// Polish plural for "miejsce": 1 → miejsce, 2-4 → miejsca, else → miejsc.
function miejscaLabel(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (n === 1) return 'miejsce';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'miejsca';
  return 'miejsc';
}

function PointsBox({
  bet,
  status,
}: {
  bet: { homeScore: number; awayScore: number; points: number | null; penaltyPoints: number | null } | undefined;
  status: 'PENDING_DRAW' | 'UPCOMING' | 'LIVE' | 'FINISHED';
}) {
  // Determine the visual tier + label.
  // - No bet → neutral, "nieobstawiony"
  // - Match not finished yet (LIVE) → neutral, "po końcu meczu"
  // - Finished + points known → success / accent / neutral by score.
  //   Total points = result (0/1/3) + penalty bonus (0/1/2).
  const noBet = !bet;
  const pointsKnown = bet && status === 'FINISHED' && bet.points != null;
  const points = pointsKnown ? bet!.points! : 0;
  const penalty = pointsKnown ? (bet!.penaltyPoints ?? 0) : 0;
  const resultPts = points - penalty;

  let bg = 'rgba(120,120,120,.06)';
  let border = '1px solid rgba(120,120,120,.18)';
  let color = '#5b6578';
  let display: string;
  let caption: string;

  if (noBet) {
    display = '0';
    caption = 'nieobstawiony';
  } else if (!pointsKnown) {
    display = '—';
    caption = 'po końcu meczu';
  } else {
    const resultCaption = resultPts >= 3 ? 'dokładny wynik' : resultPts >= 1 ? 'trafiony rezultat' : 'nietrafiony';
    const penaltyCaption = penalty === 2 ? ' + karne' : penalty === 1 ? ' + zwycięzca karnych' : '';
    display = points > 0 ? `+${points}` : '0';
    caption = resultCaption + penaltyCaption;
    if (points >= 3) {
      bg = 'rgba(31,157,85,.10)';
      border = '1px solid rgba(31,157,85,.25)';
      color = '#0f6b34';
    } else if (points >= 1) {
      bg = 'rgba(244,185,58,.12)';
      border = '1px solid rgba(244,185,58,.30)';
      color = '#6b4a00';
    }
  }

  return (
    <div style={{ background: bg, border, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, color, fontWeight: 600, letterSpacing: '.06em', marginBottom: 6 }}>
        ZDOBYTE PUNKTY
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span className="serif num" style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>
          {display}
        </span>
        <span style={{ fontSize: 11, color }}>{caption}</span>
      </div>
    </div>
  );
}
